import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  TextField,
  Button,
  IconButton,
  Box,
  Stack,
  Paper,
  Typography,
  Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import API_BASE_URL from '../config';
import { styled, alpha } from '@mui/material/styles';

// ================= Styled Components =================

const CustomFieldCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  color: 'white',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.main,
}));

// ================= Component =================

const CustomFieldComponent = ({ formData, editMode, setCustomField }) => {
  const [fields, setFields] = useState([]);

  // ✅ Fetch custom fields
  useEffect(() => {
    const fetchCustomFields = async () => {
      if (!formData?.id) return;

      try {
        // 🔥 Reset first (prevents duplication)
        setFields([]);

        const response = await axios.get(
          `${API_BASE_URL}/custom_fields/${formData.id}`,
          { withCredentials: true }
        );

        let data = response.data || [];

        // ✅ Remove duplicates by ID
        const uniqueFields = Array.from(
          new Map(data.map(item => [item.id, item])).values()
        );

        if (uniqueFields.length > 0) {
          setFields(uniqueFields);
        } else {
          setFields([createEmptyField(formData.id)]);
        }

      } catch (error) {
        console.error('Error fetching custom fields:', error);

        setFields([createEmptyField(formData.id)]);
      }
    };

    fetchCustomFields();
  }, [formData?.id]); // ✅ FIXED dependency

  // ✅ Helper: create empty field
  const createEmptyField = (userId) => ({
    id: `temp_${Date.now()}`, // unique temp id
    user_id: userId,
    title: '',
    field_name: ''
  });

  // ================= Actions =================

  const handleAddField = () => {
    setFields(prev => [...prev, createEmptyField(formData.id)]);
  };

  const handleFieldChange = (index, key, value) => {
    const updated = [...fields];
    updated[index][key] = value;

    setFields(updated);
    setCustomField(updated);
  };

//   const handleRemoveField = async (index, fieldId) => {
//     try {
//       if (fieldId && typeof fieldId === 'number') {
//         await axios.delete(`${API_BASE_URL}/custom_fields/${fieldId}`);
//       }

//       const updated = fields.filter((_, i) => i !== index);
//       setFields(updated.length ? updated : [createEmptyField(formData.id)]);
//     } catch (error) {
//       console.error('Delete error:', error);
//     }
//   };

  // ================= UI =================
const handleRemoveField = async (index, fieldId) => {
  const originalFields = [...fields];
  try {
    // ✅ STEP 1: Remove instantly from UI (BEST UX)
    const updatedFields = fields.filter((_, i) => i !== index);
    setFields(updatedFields.length ? updatedFields : [createEmptyField(formData.id)]);
    setCustomField(updatedFields);

    // ✅ STEP 2: If field exists in DB → delete API
    if (fieldId && !String(fieldId).startsWith('temp')) {
      const response = await axios.delete(`${API_BASE_URL}/custom-fields/${fieldId}`, {
        withCredentials: true
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || "Unable to delete field");
      }
    }

  } catch (error) {
    // ✅ STEP 3: Revert both states on error
    setFields(originalFields);
    setCustomField(originalFields);

    const status = error.response?.status;
    let userMessage = "Unable to delete item";
    if (status === 404) userMessage = "Item already deleted";
    else if (status === 500) userMessage = "Server error";

    alert(userMessage);
    console.error("Delete Error:", error);
  }
};


  return (
    <Box>
      <Stack spacing={3}>
        {fields.map((field, index) => (
          <CustomFieldCard key={field.id}> {/* ✅ FIXED KEY */}
            <Stack spacing={2}>

              <Stack direction="row" alignItems="center" spacing={1}>
                <SettingsIcon />
                <Typography variant="h6">
                  Custom Field {index + 1}
                </Typography>
              </Stack>

              <Divider />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>

                <StyledTextField
                  label="Field Title"
                  value={field.title || ''}
                  onChange={(e) =>
                    handleFieldChange(index, 'title', e.target.value)
                  }
                  fullWidth
                  disabled={!editMode}
                />

                <StyledTextField
                  label="Field Name"
                  value={field.field_name || ''}
                  onChange={(e) =>
                    handleFieldChange(index, 'field_name', e.target.value)
                  }
                  fullWidth
                  disabled={!editMode}
                />

                <DeleteButton
                  onClick={() => handleRemoveField(index, field.id)}
                  disabled={!editMode}
                >
                  <DeleteIcon />
                </DeleteButton>

              </Stack>
            </Stack>
          </CustomFieldCard>
        ))}

        {editMode && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ActionButton onClick={handleAddField} startIcon={<AddIcon />}>
              Add Custom Field
            </ActionButton>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default CustomFieldComponent;
